import { IUserProfile } from '@/interfaces/auth/IUserProfile';
import { z } from 'zod';

export interface IOktaLoginResult {
  outcome: 'success' | 'error' | 'denied' | 'redirect' | 'forbidden';
  message?: string;
  redirectUrl?: string;
  setCookies?: Record<string, string>;
  /**
   * On success: the provider's "this user just logged in" receipt, which can be swapped for provider
   * tokens (OKTA-9). One-time and short-lived. Server-side only — never serialise this to a client.
   */
  providerSessionToken?: string;
}

/**
 * Why a login was turned into a redirect. Mirrored in passport/src/contexts/auth/ILoginResult.ts -
 * the app uses it to route natively instead of opening redirectUrl in a webview.
 */
export const ACCOUNT_NOT_ACTIVATED = 'account-not-activated';
export type LoginRedirectReason = typeof ACCOUNT_NOT_ACTIVATED;
export const ACCOUNT_NOT_ACTIVATED_MESSAGE =
  "Your account hasn't been activated yet. We'll take you back to sign-up to finish setting it up.";

/** Shown when the password was right but the provider would not issue tokens (OKTA-9, flag on). */
export const PROVIDER_TOKENS_UNAVAILABLE_MESSAGE =
  "We couldn't sign you in just now. Please try again in a moment.";

export interface ILoginResult {
  outcome: 'success' | 'error' | 'denied' | 'redirect' | 'forbidden';
  redirectUrl?: string;
  message?: string;
  profile?: Partial<IUserProfile>;
  roles?: string[];
  setCookies?: Record<string, string>;
  reason?: LoginRedirectReason;
  // With ACCOUNT_NOT_ACTIVATED: whether the staged account still has to complete the billing
  // step before it can be verified (see IEmailStatus.needsBilling)
  needsBilling?: boolean;
}

/**
 * Will take the type from ILoginResult and replace the fields that *might* not exist if we weren't successful
 * with the fields that *will* exist if we were successful.
 */
export interface ISuccessfulLoginResult extends Omit<ILoginResult, 'outcome' | 'profile'> {
  outcome: 'success';
  profile: IUserProfile;
}

export const loginResultSchema: z.ZodType<Omit<ILoginResult, 'profile'>> = z.object({
  outcome: z.enum(['success', 'error', 'denied', 'redirect', 'forbidden']),
  redirectUrl: z.string().optional(),
  message: z.string().optional(),
  roles: z.array(z.string()).optional(),
  setCookies: z.record(z.string()).optional(),
  reason: z.literal(ACCOUNT_NOT_ACTIVATED).optional(),
  needsBilling: z.boolean().optional(),
});
